/**
 * servicoChatSocket
 *
 * RESPONSABILIDADE:
 * - Gerenciar conexão WebSocket com o backend para o chat de edição e eventos
 *   em tempo real relacionados ao processo (ex.: atualizações da minuta).
 *
 * CONTEXTO DE NEGÓCIO:
 * - O chat permite que o advogado converse com a IA e solicite alterações no
 *   documento. As respostas e eventos são entregues pelo backend via WebSocket.
 *
 * PADRÕES:
 * - Conexão resiliente com reconexão exponencial
 * - API simples para enviar mensagens e assinar callbacks de eventos
 * - Tolerância a diferentes formatos de payload do backend
 * - Autenticação JWT via parâmetro de query na URL WebSocket
 */

export type ChatAutor = 'usuario' | 'ia' | 'sistema';

export interface ChatMensagemEvento {
  tipo?: string; // 'mensagem_chat' | 'resposta_ia' | 'sistema' | ...
  autor?: ChatAutor;
  conteudo?: string;
  timestamp?: string; // ISO 8601
  // Campos extras tolerados
  [k: string]: unknown;
}

export interface EventoAtualizacaoMinuta {
  tipo?: string; // 'minuta_atualizada' | 'document_update'
  id_minuta?: string;
  conteudo_markdown?: string;
  conteudo_html?: string;
  timestamp?: string;
  [k: string]: unknown;
}

export interface ChatSocketCallbacks {
  onOpen?: () => void;
  onClose?: (ev?: CloseEvent) => void;
  onError?: (ev?: Event) => void;
  onMensagem?: (evento: ChatMensagemEvento) => void;
  onAtualizacaoMinuta?: (evento: EventoAtualizacaoMinuta) => void;
}

export interface ChatSocketConexao {
  enviarMensagem: (conteudo: string, meta?: Record<string, unknown>) => void;
  fechar: () => void;
}

/**
 * Gera a URL de WebSocket (ws/wss) a partir de VITE_API_URL e do caminho
 * Inclui token JWT como parâmetro de query para autenticação
 */
function construirUrlWs(path: string, token?: string): string {
  const baseHttp = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');
  const url = new URL(baseHttp);
  const protocoloWs = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const urlWs = `${protocoloWs}//${url.host}${path.startsWith('/') ? path : `/${path}`}`;

  // Adicionar token JWT como parâmetro de query se disponível
  if (token) {
    const urlComToken = new URL(urlWs);
    urlComToken.searchParams.set('token', token);
    return urlComToken.toString();
  }

  return urlWs;
}

/**
 * Cria uma conexão WebSocket resiliente para o chat de um processo.
 * - O endpoint esperado é: ws:///api/processos/{processo_id}/chat
 * - Inclui token JWT na URL para autenticação se fornecido
 */
export function criarChatSocket(
  processoId: string,
  callbacks: ChatSocketCallbacks = {},
  token?: string
): ChatSocketConexao {
  const caminho = `/api/processos/${encodeURIComponent(processoId)}/chat`;
  const url = construirUrlWs(caminho, token);

  let ws: WebSocket | null = null;
  let tentativas = 0;
  let fechadoManual = false;
  let timeoutId: number | undefined;

  const conectar = () => {
    ws = new WebSocket(url);

    ws.onopen = () => {
      tentativas = 0;
      callbacks.onOpen?.();
    };

    ws.onmessage = (event: MessageEvent) => {
      const dadosBrutos = event.data;
      try {
        const payload = typeof dadosBrutos === 'string' ? dadosBrutos : String(dadosBrutos);
        let obj: Record<string, unknown> | null = null;
        try {
          obj = JSON.parse(payload);
        } catch {
          // Mensagem de texto simples: tratar como resposta da IA
          callbacks.onMensagem?.({ tipo: 'resposta_ia', autor: 'ia', conteudo: payload, timestamp: new Date().toISOString() });
          return;
        }

        // Roteamento por tipo (tolerante a variantes)
        const tipo = String((obj as { tipo?: string }).tipo || '').toLowerCase();
        if (tipo.includes('minuta') || tipo.includes('document')) {
          callbacks.onAtualizacaoMinuta?.(obj as unknown as EventoAtualizacaoMinuta);
          return;
        }

        // Mensagem de chat padrão
        callbacks.onMensagem?.(obj as unknown as ChatMensagemEvento);
      } catch (err) {
        console.warn('[servicoChatSocket] Falha ao processar mensagem WS:', err);
      }
    };

    ws.onerror = (ev) => {
      callbacks.onError?.(ev);
    };

    ws.onclose = (ev) => {
      callbacks.onClose?.(ev);
      if (fechadoManual) return;
      // Reconexão exponencial com teto de 10s
      tentativas += 1;
      const atraso = Math.min(10000, 500 * Math.pow(2, tentativas));
      timeoutId = window.setTimeout(() => conectar(), atraso);
    };
  };

  conectar();

  return {
    enviarMensagem: (conteudo: string, meta: Record<string, unknown> = {}) => {
      try {
        const msg = {
          tipo: 'mensagem_chat',
          conteudo,
          timestamp: new Date().toISOString(),
          ...meta,
        };
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(msg));
        } else {
          console.warn('[servicoChatSocket] WebSocket não está aberto. Ignorando envio.');
        }
      } catch (err) {
        console.warn('[servicoChatSocket] Erro ao enviar mensagem WS:', err);
      }
    },
    fechar: () => {
      fechadoManual = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(); } catch { /* ignore */ }
      }
      ws = null;
    },
  };
}
